import React, { useState } from 'react';
import { apiPost } from '../utils/api';
import { BTN_XS } from '../constants/buttons';
import { FORM_INPUT_FIELD } from '../constants/inputs';
import useSubmitGuard from '../hooks/useSubmitGuard';
import { friendlyErrorMessage } from './ErrorMessage';

/**
 * Invite / Resend on a department CARD's member row (owner 2026-11-03, chose
 * "Inline Invite on every card" — the familiar members-list pattern: a quiet
 * green "Invite" beside a not-yet-invited person, "Resend" once invited).
 *
 * `POST /api/users/:id/invite` with `department_id` = THIS card's department:
 * the server asks its person gate of that department (manages it, the person
 * is in it, never a My Space). A person with no email gets a one-line email box
 * first — the route needs an address to send to.
 *
 * Renders nothing for an Active / Deactivated member or a row with no account.
 * The PAGE decides who sees it (`show`), mirroring that server gate.
 */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function MemberInviteButton({ member, deptId, show, onDone, testIdSuffix = '' }) {
  const [asking, setAsking] = useState(false);
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const { guard, busy } = useSubmitGuard();
  if (!show || !member?.user_id || !['placeholder', 'invited'].includes(member.status)) return null;

  const label = member.status === 'invited' ? 'Resend' : 'Invite';
  const id = `${member.user_id}${testIdSuffix}`;

  const send = (address) => guard(async () => {
    try {
      setError('');
      const res = await apiPost(`/api/users/${member.user_id}/invite`, {
        department_id: Number(deptId),
        ...(address ? { email: address } : {}),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not send the invite');
      setAsking(false); setEmail('');
      const to = data.email || address || member.email;
      onDone(data.email_warning
        ? { message: `${member.username} is marked invited, but the email to ${to} could not be sent. Ask them to use "Forgot password".`, tone: 'warning' }
        : { message: `Invite sent to ${to}.`, tone: 'success' });
    } catch (err) {
      setError(friendlyErrorMessage(err));
    }
  });

  const onClick = (e) => {
    e.stopPropagation();
    if (member.email) send(null);
    else setAsking(true);
  };

  return (
    <span className="inline-flex flex-col gap-1 min-w-0" onClick={(e) => e.stopPropagation()}>
      {!asking && (
        <button type="button" onClick={onClick} disabled={busy}
          title={member.status === 'invited' ? `Email ${member.username} the invite again` : `Email ${member.username} a link to set a password and sign in`}
          className="inline-flex items-center justify-center min-h-[44px] min-w-[44px] md:min-h-0 md:min-w-0 px-1 text-xs font-semibold text-green-700 dark:text-green-300 hover:underline disabled:opacity-50"
          data-testid={`member-invite-${id}`}>
          {busy ? 'Sending…' : label}
        </button>
      )}
      {asking && (
        <span className="flex flex-wrap items-center gap-1" data-testid={`member-invite-ask-${id}`}>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus
            placeholder={`Email for ${member.username}`} aria-label={`Email for ${member.username}`}
            className={`${FORM_INPUT_FIELD} w-56 max-w-full`} data-testid={`member-invite-email-${id}`} />
          <button type="button" onClick={() => send(email.trim())} disabled={busy || !EMAIL_RE.test(email.trim())}
            className={`${BTN_XS.confirm} min-h-[44px] md:min-h-0 disabled:opacity-50 disabled:cursor-not-allowed`}
            data-testid={`member-invite-send-${id}`}>
            {busy ? 'Sending…' : 'Send invite'}
          </button>
          <button type="button" onClick={() => { setAsking(false); setEmail(''); setError(''); }}
            className={`${BTN_XS.secondary} min-h-[44px] md:min-h-0`}>
            Cancel
          </button>
        </span>
      )}
      {error && <span role="alert" className="text-xs text-red-700 dark:text-red-300">{error}</span>}
    </span>
  );
}
